import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { REPORT_TYPE } from 'constants/constantVariables';
import AuthService from 'tools/AuthService';
import MonitoringSettings from './monitoringSettings';
import './monitoringLedger.scss';

// The monitoring alerts ledger: the live alerts feed with acknowledge, summary tiles, server-side
// filtering/paging, per-change severity and per-business risk, business filter and CSV export.

const PAGE_SIZE = 10;

const SOURCE_LABEL = {
    [REPORT_TYPE.LEXIS_VERIFICATION]: 'VERIFICATION',
    [REPORT_TYPE.LEXIS_BACKGROUND]: 'BACKGROUND',
    [REPORT_TYPE.UCC]: 'UCC',
    [REPORT_TYPE.FMCSA]: 'FMCSA',
    [REPORT_TYPE.SOS]: 'SOS',
};

// Dropdown label -> backend RoxReportType, for server-side source filtering. Note: alerts with a
// null reportType (older rows) can't be matched server-side, so a source filter may miss those.
const SOURCE_TO_REPORT_TYPE = {
    VERIFICATION: REPORT_TYPE.LEXIS_VERIFICATION,
    BACKGROUND: REPORT_TYPE.LEXIS_BACKGROUND,
    UCC: REPORT_TYPE.UCC,
    FMCSA: REPORT_TYPE.FMCSA,
    SOS: REPORT_TYPE.SOS,
};
const SOURCE_OPTIONS = ['ALL', 'VERIFICATION', 'BACKGROUND', 'UCC', 'FMCSA', 'SOS'];

// Date-range options -> lookback in days (null = no lower bound). Sent to the backend as `from`.
const DATE_RANGES = { ALL: null, LAST_7: 7, LAST_30: 30, LAST_90: 90, LAST_365: 365 };
const DATE_RANGE_LABELS = {
    ALL: 'All time',
    LAST_7: 'Last 7 days',
    LAST_30: 'Last 30 days',
    LAST_90: 'Last 90 days',
    LAST_365: 'Last 12 months',
};

const SOURCE_PROVENANCE = {
    UCC: 'Nationwide UCC index',
    SOS: 'Secretary of State',
    FMCSA: 'FMCSA SAFER',
    VERIFICATION: 'LexisNexis verification',
    BACKGROUND: 'LexisNexis background',
};

// reportType is often null on older alerts, so fall back to a keyword read of the description.
const deriveSource = (item) => {
    if (item.reportType && SOURCE_LABEL[item.reportType]) return SOURCE_LABEL[item.reportType];
    const d = (item.description || '').toLowerCase();
    if (d.includes('background')) return 'BACKGROUND';
    if (d.includes('ucc')) return 'UCC';
    if (d.includes('fmcsa')) return 'FMCSA';
    if (d.includes('verification')) return 'VERIFICATION';
    if (d.includes('sos') || d.includes('secretary') || d.includes('status')) return 'SOS';
    return '—';
};

// The application page deep-links to a report via ?tab=<slug> (see findReportBySlug in ApplicantDetail).
// Map a monitored report type — or the derived source when reportType is null on older alerts — to that
// slug so "View application" lands on the report that changed instead of the top of the application.
const REPORT_TYPE_TO_SLUG = {
    [REPORT_TYPE.FMCSA]: 'fmcsa',
    [REPORT_TYPE.SOS]: 'sos',
    [REPORT_TYPE.UCC]: 'ucc',
    [REPORT_TYPE.LEXIS_VERIFICATION]: 'verification',
    [REPORT_TYPE.LEXIS_BACKGROUND]: 'bgv',
};
const SOURCE_TO_SLUG = { FMCSA: 'fmcsa', SOS: 'sos', UCC: 'ucc', VERIFICATION: 'verification', BACKGROUND: 'bgv' };
const applicantHref = (item) => {
    if (!item || !item.applicationId) return null;
    const slug = (item.reportType && REPORT_TYPE_TO_SLUG[item.reportType]) || SOURCE_TO_SLUG[deriveSource(item)];
    return `/applicant/${item.applicationId}${slug ? `?tab=${slug}` : ''}`;
};

// Best-effort parse of a change sentence into {field, before, after}. Fallback for alerts created
// before structured change records existed; newer alerts carry `structuredChanges` natively.
const parseChange = (sentence) => {
    let m = sentence.match(/^(.*?)\s+changed from\s+(.*?)\s+to\s+(.*)$/i);
    if (m) return { field: m[1].replace(/\s+count$/i, '').trim(), before: m[2].trim(), after: m[3].trim() };
    m = sentence.match(/^New\s+(.*?)\s+added:\s+(.*)$/i);
    if (m) return { field: m[1].trim(), before: '—', after: m[2].trim() };
    m = sentence.match(/^(.*?)\s+removed\.\s+Previous count was:\s+(.*)$/i);
    if (m) return { field: m[1].trim(), before: m[2].trim(), after: '—' };
    return null;
};

const SEVERITY_CLASS = { ADVERSE: 'sev-adverse', WARNING: 'sev-warning', INFORMATIONAL: 'sev-informational' };
const SeverityBadge = ({ severity }) => (
    <span className={`ledger__sevBadge ${SEVERITY_CLASS[severity] || ''}`}>{severity}</span>
);

// Per-business risk = the highest severity among its unacknowledged alerts, mapped to a risk label.
const SEVERITY_RANK = { ADVERSE: 3, WARNING: 2, INFORMATIONAL: 1 };
const RISK_FROM_SEVERITY = {
    ADVERSE: { label: 'ELEVATED RISK', cls: 'risk-elevated' },
    WARNING: { label: 'MODERATE RISK', cls: 'risk-moderate' },
    INFORMATIONAL: { label: 'LOW RISK', cls: 'risk-low' },
};

// "Why this matters" — plain-English underwriting rationale keyed by the changed field (from the
// structured change records, or parsed from the change text for older alerts). Keys are lower-cased.
const WHY_BY_FIELD = {
    'records': 'The number of public records on file changed — new filings can signal emerging financial or legal exposure.',
    'liens & judgements': 'A change in liens & judgements can indicate unpaid debts or court rulings against the business.',
    'uccs': 'A change in UCC filings reflects new secured lenders or released collateral — it affects borrowing capacity and existing lien position.',
    'ucc filings': 'A change in UCC filings reflects new secured lenders or released collateral — it affects borrowing capacity and existing lien position.',
    'bankruptcies': 'A new or removed bankruptcy is a strong solvency signal and materially changes credit risk.',
    'criminal records': 'A change in the owner\'s criminal records is a character/integrity flag that usually warrants manual review.',
    'status': 'A Secretary of State status change (e.g. active → not in good standing) can mean the entity is no longer legally able to operate.',
    'safety rating': 'An FMCSA safety-rating change can affect the carrier\'s authority to operate and its insurability.',
    'insurance on file': 'A drop in insurance on file may leave the carrier non-compliant and uninsured against loss.',
    'bipd required amount': 'A change in required BIPD insurance affects the carrier\'s compliance with federal coverage minimums.',
    'bipd required': 'A change in required BIPD insurance affects the carrier\'s compliance with federal coverage minimums.',
    'operating authority': 'An operating-authority change can mean the carrier\'s legal ability to operate has changed.',
    'business verification': 'A change in business verification lowers confidence that the business is who it claims to be.',
    'verification index': 'A change in the verification index lowers confidence that the business is who it claims to be.',
    'verification description': 'A change in the verification result lowers confidence that the business is who it claims to be.',
};

// Fallback rationale when no changed field maps, keyed by the alert's severity.
const WHY_BY_SEVERITY = {
    ADVERSE: 'This change is classified Adverse — it typically signals materially higher risk and warrants review before continuing.',
    WARNING: 'This change is classified Warning — worth a look to confirm it does not affect your decision.',
    INFORMATIONAL: 'This change is classified Informational — logged for the record; usually no action needed.',
};

const ACTION_BY_SEVERITY = {
    ADVERSE: 'Recommended: review the change and confirm the account still meets your risk criteria before acknowledging.',
    WARNING: 'Recommended: verify the change, then acknowledge once reviewed.',
    INFORMATIONAL: 'Recommended: acknowledge to clear this from the review queue.',
};

// Build the "why this matters" content: one reason per distinct changed field, plus a recommended action.
const whyItMatters = (fields, severity) => {
    const reasons = [];
    const seen = new Set();
    (fields || []).forEach((f) => {
        // Normalize "and" -> "&" so parsed-text fields ("Liens and judgements") match the structured
        // field labels ("Liens & judgements") used as map keys.
        const key = (f || '').trim().toLowerCase().replace(/\s+and\s+/g, ' & ');
        const text = WHY_BY_FIELD[key];
        if (text && !seen.has(key)) {
            seen.add(key);
            reasons.push(text);
        }
    });
    if (!reasons.length) {
        reasons.push(WHY_BY_SEVERITY[severity] || 'A monitored data source reported a change on this business.');
    }
    return { reasons, action: ACTION_BY_SEVERITY[severity] || 'Acknowledge to clear this from the review queue.' };
};

const MonitoringLedger = ({
    monitoringList = [],
    history,
    summary,
    alertHeaders = {},
    onQuery,
    onAcknowledge,
    onAcknowledgeMany,
    onAddNote,
    onHideNote,
    getSeverityConfig,
    updateSeverityConfig,
    getFieldCatalog,
    getWatchedFields,
    updateWatchedFields,
    getBusinesses,
    getDevEnabled,
    onCreateTestAlert,
    readOnly = false,
}) => {
    const [acknowledged, setAcknowledged] = useState(() => new Set());
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sourceFilter, setSourceFilter] = useState('ALL');
    const [businesses, setBusinesses] = useState([]);
    const [businessFilter, setBusinessFilter] = useState('ALL');
    const [groupByBusiness, setGroupByBusiness] = useState(true);
    const [unackOnly, setUnackOnly] = useState(false);
    const [dateRange, setDateRange] = useState('ALL');
    const [page, setPage] = useState(0);
    // Last filter signature seen by the query effect, to skip the stale pre-page-reset request (see below).
    const lastFilterSig = useRef(null);
    const [selected, setSelected] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [refreshTick, setRefreshTick] = useState(0);
    const currentUserEmail = (AuthService.loggedInUser?.email || AuthService.getProfile()?.email || '').toLowerCase();

    // Dev-only scenario builder state.
    const [devEnabled, setDevEnabled] = useState(false);
    const [devOpen, setDevOpen] = useState(false);
    const [testSource, setTestSource] = useState('BACKGROUND');
    const [testSeverity, setTestSeverity] = useState('ADVERSE');
    const [testAck, setTestAck] = useState(false);
    const [testBiz, setTestBiz] = useState('');
    const [creatingTest, setCreatingTest] = useState(false);

    // Debounce the search box so we don't query on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    // Load the "filter by business" options once. Fails soft (endpoint may not be deployed).
    useEffect(() => {
        if (!getBusinesses) return;
        let cancelled = false;
        Promise.resolve(getBusinesses())
            .then((res) => { if (!cancelled && Array.isArray(res)) setBusinesses(res); })
            .catch(() => { /* leave dropdown at "All businesses" */ });
        return () => { cancelled = true; };
    }, [getBusinesses]);

    // Is the dev scenario builder available in this environment? Fails soft (endpoint may be off/absent).
    useEffect(() => {
        if (!getDevEnabled) return;
        Promise.resolve(getDevEnabled())
            .then((v) => setDevEnabled(v === true))
            .catch(() => { /* leave the panel hidden */ });
    }, [getDevEnabled]);

    // Default the scenario builder's business to the first loaded option.
    useEffect(() => {
        if (!testBiz && businesses.length) setTestBiz(businesses[0].name);
    }, [businesses, testBiz]);

    // Any filter change resets to the first page.
    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, sourceFilter, businessFilter, unackOnly, dateRange]);

    // Server-side query: fires on paging, filter, or post-acknowledge refresh.
    useEffect(() => {
        if (!onQuery) return;
        // When a filter changes while on a later page, the effect above schedules setPage(0) but this
        // effect also runs this render with the OLD page — issuing a stale request whose (later-arriving)
        // response could overwrite the page-0 results. Skip that pre-reset request; the page-0 render fires
        // the real one. Only guards the filter-change case, so pagination and refresh still query normally.
        const filterSig = `${debouncedSearch}|${sourceFilter}|${businessFilter}|${unackOnly}|${dateRange}`;
        const filtersChanged = filterSig !== lastFilterSig.current;
        lastFilterSig.current = filterSig;
        if (filtersChanged && page !== 0) {
            return;
        }
        const days = DATE_RANGES[dateRange];
        onQuery({
            page,
            limit: PAGE_SIZE,
            reportType: sourceFilter === 'ALL' ? undefined : SOURCE_TO_REPORT_TYPE[sourceFilter],
            acknowledged: unackOnly ? false : undefined,
            search: debouncedSearch || undefined,
            from: days ? moment().subtract(days, 'days').toISOString() : undefined,
            businessName: businessFilter === 'ALL' ? undefined : businessFilter,
        });
    }, [page, debouncedSearch, sourceFilter, businessFilter, unackOnly, dateRange, refreshTick, onQuery]);

    const isAck = (item) => acknowledged.has(item.uuid) || item.status === 'REVIEWED';

    // Export the current (server-filtered) view as CSV.
    const csvCell = (value) => {
        const raw = String(value == null ? '' : value);
        const s = /^[\t\r ]*[=+\-@]/.test(raw) ? `'${raw}` : raw;
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const exportCsv = () => {
        const header = ['Business', 'Source', 'What changed', 'Details', 'Detected', 'Severity', 'State'];
        const body = monitoringList.map((item) => [
            item.businessName,
            deriveSource(item),
            item.description,
            (item.changes || []).join('; '),
            item.createdAt ? moment(item.createdAt).format('YYYY-MM-DD HH:mm') : '',
            item.severity || '',
            isAck(item) ? 'Acknowledged' : 'Needs review',
        ]);
        const csv = [header, ...body].map((row) => row.map(csvCell).join(',')).join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `monitoring-alerts-${moment().format('YYYY-MM-DD')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const markLocal = (uuids) => setAcknowledged((prev) => {
        const next = new Set(prev);
        uuids.forEach((u) => next.add(u));
        return next;
    });
    const ackOne = async (item, note) => {
        // Mark acknowledged locally only AFTER the PATCH succeeds; on failure the row stays in review
        // (an optimistic mark with no rollback could silently drop the item until a full remount).
        if (readOnly) return;
        if (onAcknowledge) await onAcknowledge(item.uuid, note);
        markLocal([item.uuid]);
        setRefreshTick((t) => t + 1);
    };
    // Add an attributed note without acknowledging, so the alert stays in review.
    const addNote = async () => {
        const text = noteText.trim();
        if (readOnly || !text || !onAddNote || !selected) return;
        setSavingNote(true);
        try {
            const updated = await onAddNote(selected.uuid, text);
            if (updated) setSelected(updated); // reflect the new note (with id/author/time) immediately
            setNoteText('');
            setRefreshTick((t) => t + 1);
        } finally {
            setSavingNote(false);
        }
    };
    // Hide a note (soft-delete). Only the author sees the control; the backend also enforces it.
    const hideNote = async (noteId) => {
        if (readOnly || !onHideNote || !selected) return;
        const updated = await onHideNote(selected.uuid, noteId);
        if (updated) setSelected(updated);
        setRefreshTick((t) => t + 1);
    };
    const ackMany = async (uuids) => {
        // Same as ackOne: only mark locally after the bulk PATCH succeeds, so a failure keeps the rows.
        if (readOnly) return;
        if (onAcknowledgeMany) await onAcknowledgeMany(uuids);
        markLocal(uuids);
        setRefreshTick((t) => t + 1);
    };

    // Dev scenario builder: create one test alert, then refresh the ledger + tiles so it shows up.
    const createTestAlert = async () => {
        const businessName = testBiz || (businesses[0] && businesses[0].name);
        if (readOnly || !onCreateTestAlert || !businessName) return;
        setCreatingTest(true);
        try {
            await onCreateTestAlert({
                businessName,
                reportType: SOURCE_TO_REPORT_TYPE[testSource],
                severity: testSeverity,
                acknowledged: testAck,
            });
            setRefreshTick((t) => t + 1);
        } finally {
            setCreatingTest(false);
        }
    };

    // The server already filtered/paged; just group the returned page by business.
    const groups = useMemo(() => {
        const map = new Map();
        monitoringList.forEach((item) => {
            if (!map.has(item.applicationId)) {
                map.set(item.applicationId, {
                    applicationId: item.applicationId,
                    businessName: item.businessName,
                    since: item.monitoringStartDate,
                    items: [],
                });
            }
            const g = map.get(item.applicationId);
            g.items.push(item);
            if (item.monitoringStartDate && (!g.since || new Date(item.monitoringStartDate) < new Date(g.since))) {
                g.since = item.monitoringStartDate;
            }
        });
        return Array.from(map.values());
    }, [monitoringList]);

    const totalRecords = Number(alertHeaders['x-pagination-total-records'] || 0);
    const totalPages = Number(alertHeaders['x-pagination-total-pages'] || 0);
    const hasNext = totalPages ? page < totalPages - 1 : monitoringList.length >= PAGE_SIZE;

    // Fallbacks for the tiles if the summary endpoint hasn't answered yet.
    const businessesDerived = new Set(monitoringList.map((i) => i.applicationId)).size;
    const unackDerived = monitoringList.filter((i) => !isAck(i)).length;

    const selectedSource = selected ? deriveSource(selected) : null;
    const selectedAcked = selected ? isAck(selected) : false;
    const hasStructured = !!(selected && selected.structuredChanges && selected.structuredChanges.length);
    const changedFields = !selected
        ? []
        : hasStructured
            ? selected.structuredChanges.map((c) => c.field)
            : (selected.changes || []).map((c) => { const p = parseChange(c); return p ? p.field : null; }).filter(Boolean);
    const why = selected ? whyItMatters(changedFields, selected.severity) : null;

    // Shared alert row, used by both the grouped and the flat (ungrouped) views.
    const renderRow = (item, { showBusiness = false } = {}) => {
        const source = deriveSource(item);
        const acked = isAck(item);
        return (
            <div className={`ledger__row ${acked ? 'is-ack' : ''}`} key={item.uuid} onClick={() => { setSelected(item); setNoteText(''); }}>
                <span className="ledger__source"><span className="ledger__badge">{source}</span></span>
                <span className="ledger__what">
                    <span className="ledger__whatTitle">{item.description}</span>
                    {showBusiness && item.businessName &&
                        <span className="ledger__rowBiz" onClick={(e) => { e.stopPropagation(); history.push(`/applicant/${item.applicationId}`); }}>{item.businessName}</span>}
                    {(item.changes || []).map((c, i) => <span className="ledger__change" key={i}>{c}</span>)}
                </span>
                <span className="ledger__detected">
                    <span>{item.createdAt ? moment(item.createdAt).format('MMM DD, YYYY, HH:mm') : '—'}</span>
                    <span className="ledger__prov">{SOURCE_PROVENANCE[source] || 'Monitoring sweep'}</span>
                </span>
                <span className="ledger__state">
                    {acked
                        ? <span className="ledger__stateBadge is-ack">ACKNOWLEDGED</span>
                        : item.severity
                            ? <SeverityBadge severity={item.severity} />
                            : <span className="ledger__stateBadge is-review">NEEDS REVIEW</span>}
                </span>
                <span className="ledger__action" onClick={(e) => e.stopPropagation()}>
                    {acked
                        ? <span className="ledger__ackedBy">{item.acknowledgedBy || 'You'} · {item.acknowledgedAt ? moment(item.acknowledgedAt).format('MMM DD') : moment().format('MMM DD')}</span>
                        : !readOnly && <button className="ledger__ackBtn" onClick={() => ackOne(item)}>Acknowledge</button>}
                </span>
            </div>
        );
    };

    return (
        <div className="ledger">
            {devEnabled && (
                <div className="ledger__dev">
                    <button className="ledger__devToggle" onClick={() => setDevOpen((v) => !v)}>
                        <span className="ledger__devTag">DEV</span> Scenario builder — generate a test alert
                        <span className="ledger__devChevron">{devOpen ? '▲' : '▼'}</span>
                    </button>
                    {devOpen && (
                        <div className="ledger__devBody">
                            <label className="ledger__devField">
                                <span>Business</span>
                                <select value={testBiz} onChange={(e) => setTestBiz(e.target.value)} disabled={!businesses.length}>
                                    {businesses.map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}
                                </select>
                            </label>
                            <label className="ledger__devField">
                                <span>Source</span>
                                <select value={testSource} onChange={(e) => setTestSource(e.target.value)}>
                                    {SOURCE_OPTIONS.filter((s) => s !== 'ALL').map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </label>
                            <label className="ledger__devField">
                                <span>Severity</span>
                                <select value={testSeverity} onChange={(e) => setTestSeverity(e.target.value)}>
                                    <option value="ADVERSE">Adverse</option>
                                    <option value="WARNING">Warning</option>
                                    <option value="INFORMATIONAL">Informational</option>
                                </select>
                            </label>
                            <label className="ledger__devField">
                                <span>State</span>
                                <select value={testAck ? 'ACK' : 'REVIEW'} onChange={(e) => setTestAck(e.target.value === 'ACK')}>
                                    <option value="REVIEW">Needs review</option>
                                    <option value="ACK">Acknowledged</option>
                                </select>
                            </label>
                            <div className="ledger__devField ledger__devField--action">
                                <span aria-hidden="true">&nbsp;</span>
                                <button className="ledger__devCreate" onClick={createTestAlert} disabled={readOnly || creatingTest || !businesses.length}>
                                    {creatingTest ? 'Creating…' : 'Create test alert'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="ledger__header">
                <div>
                    <h2 className="ledger__title">Monitoring</h2>
                    <p className="ledger__subtitle">Every change detected across your portfolio, with its source and timestamp.</p>
                </div>
                <div className="ledger__headerActions">
                    <button className="ledger__ghostBtn" onClick={exportCsv} title="Export the current view as CSV">Export log</button>
                    {!readOnly && <button className="ledger__ghostBtn" onClick={() => setSettingsOpen(true)}>Monitoring settings</button>}
                </div>
            </div>

            <div className="ledger__stats">
                <div className="ledger__stat">
                    <span>Businesses monitored</span>
                    <strong>{summary ? summary.businessesMonitored : businessesDerived}</strong>
                    {summary
                        ? <em className="ledger__statSub">actively monitored</em>
                        : <em className="ledger__statSub">with alerts (loaded page)</em>}
                </div>
                <div className="ledger__stat">
                    <span>Changes {summary ? '(7 days)' : '(loaded)'}</span>
                    <strong>{summary ? summary.changesLast7Days : monitoringList.length}</strong>
                </div>
                <div className="ledger__stat">
                    <span>Unacknowledged</span>
                    <strong className="ledger__stat--warn">{summary ? summary.unacknowledged : unackDerived}</strong>
                </div>
                <div className="ledger__stat">
                    <span>Checked (30d)</span>
                    <strong className={summary && summary.overdueCount ? 'ledger__stat--warn' : ''}>
                        {summary ? `${summary.businessesFresh}/${summary.businessesMonitored}` : '—'}
                    </strong>
                    {summary
                        ? (summary.overdueCount
                            ? <em className="ledger__statSub ledger__statSub--warn">{summary.overdueCount} business{summary.overdueCount === 1 ? '' : 'es'} overdue</em>
                            : <em className="ledger__statSub">all up to date</em>)
                        : <em className="ledger__statSub">coverage</em>}
                </div>
            </div>

            <div className="ledger__filters">
                <input
                    className="ledger__search"
                    placeholder="Search changes…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <select className="ledger__select" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                    {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'All sources' : s}</option>)}
                </select>
                <select className="ledger__select" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                    {Object.keys(DATE_RANGES).map((k) => <option key={k} value={k}>{DATE_RANGE_LABELS[k]}</option>)}
                </select>
                <select
                    className="ledger__select"
                    value={businessFilter}
                    onChange={(e) => setBusinessFilter(e.target.value)}
                    disabled={!businesses.length}
                    title={businesses.length ? 'Filter by business' : 'No businesses to filter yet'}
                >
                    <option value="ALL">All businesses</option>
                    {businesses.map((b) => (
                        <option key={b.name} value={b.name}>
                            {b.name}{b.monitoredSince ? ` · since ${moment(b.monitoredSince).format('MMM YYYY')}` : ''}
                        </option>
                    ))}
                </select>
                <button
                    className={`ledger__chip ${unackOnly ? 'is-active' : ''}`}
                    onClick={() => setUnackOnly((v) => !v)}
                >
                    Unacknowledged
                </button>
                <label className="ledger__chip" title="Group alerts under each business, or show a flat list">
                    <input type="checkbox" checked={groupByBusiness} onChange={(e) => setGroupByBusiness(e.target.checked)} /> Group by business
                </label>
            </div>

            <div className="ledger__scroll">
                <div className="ledger__table">
                    <div className="ledger__head">
                        <span>Source</span><span>What changed</span><span>Detected</span><span>State</span><span>Action</span>
                    </div>

                    {monitoringList.length === 0 && <div className="ledger__empty">No changes match your filters.</div>}

                    {!groupByBusiness && monitoringList.map((item) => renderRow(item, { showBusiness: true }))}

                    {groupByBusiness && groups.map((group) => {
                        const groupUnack = group.items.filter((i) => !isAck(i)).map((i) => i.uuid);
                        // Prefer the server's portfolio-wide worst open severity for this business (page-
                        // independent); fall back to reducing the visible page when the summary isn't loaded.
                        const pageTopSeverity = group.items.reduce((top, i) => (
                            !isAck(i) && i.severity && (!top || SEVERITY_RANK[i.severity] > SEVERITY_RANK[top]) ? i.severity : top
                        ), null);
                        const serverTopSeverity = summary
                            && summary.topSeverityByApplication
                            && summary.topSeverityByApplication[group.applicationId];
                        const topSeverity = serverTopSeverity || pageTopSeverity;
                        const risk = topSeverity ? RISK_FROM_SEVERITY[topSeverity] : null;
                        const cadence = group.items[0] || {};
                        return (
                            <div className="ledger__group" key={group.applicationId}>
                                <div className="ledger__groupHead">
                                    <span className="ledger__biz" onClick={() => history.push(`/applicant/${group.applicationId}`)}>
                                        {group.businessName}
                                    </span>
                                    {risk && <span className={`ledger__riskBadge ${risk.cls}`}>{risk.label}</span>}
                                    <span className="ledger__meta">
                                        {group.items.length} change{group.items.length > 1 ? 's' : ''}
                                        {cadence.lastChecked ? ` · last checked ${moment(cadence.lastChecked).format('MMM DD')}` : ''}
                                        {cadence.nextSweep ? ` · next ${moment(cadence.nextSweep).format('MMM DD')}` : ''}
                                    </span>
                                    {!readOnly && groupUnack.length > 0 && (
                                        <button
                                            className="ledger__ackAll"
                                            title="Acknowledges only this business's changes shown on the current page; alerts on other pages are unaffected."
                                            onClick={() => ackMany(groupUnack)}
                                        >
                                            Acknowledge {groupUnack.length} shown
                                        </button>
                                    )}
                                    <span className="ledger__openFile" onClick={() => history.push(`/applicant/${group.applicationId}`)}>
                                        View application →
                                    </span>
                                </div>

                                {group.items.map((item) => renderRow(item))}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="ledger__footer">
                <span>
                    {totalRecords} change{totalRecords === 1 ? '' : 's'} · page {page + 1}{totalPages ? ` of ${totalPages}` : ''}
                </span>
                <span className="ledger__pager">
                    <button disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
                    <button disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>Next</button>
                </span>
            </div>

            {selected && (
                <>
                    <div className="ledger__backdrop" onClick={() => setSelected(null)} />
                    <aside className="ledger__panel">
                        <div className="ledger__panelHead">
                            <span className="ledger__panelSource">{selectedSource} · {selected.businessName}</span>
                            <button className="ledger__panelClose" onClick={() => setSelected(null)}>×</button>
                        </div>
                        <h3 className="ledger__panelTitle">{selected.description}</h3>
                        <div className="ledger__panelBadges">
                            {selected.severity && <SeverityBadge severity={selected.severity} />}
                            <span className={`ledger__stateBadge ${selectedAcked ? 'is-ack' : 'is-review'}`}>
                                {selectedAcked ? 'ACKNOWLEDGED' : 'NEEDS REVIEW'}
                            </span>
                        </div>

                        <div className="ledger__panelSection">
                            <span className="ledger__panelLabel">The change</span>
                            <div className="ledger__diffTable">
                                <div className="ledger__diffHead"><span>Field</span><span>Before → after</span></div>
                                {hasStructured
                                    ? selected.structuredChanges.map((c, i) => (
                                        <div className="ledger__diffRow" key={i}>
                                            <span className="ledger__diffField">{c.field}</span>
                                            <span className="ledger__diffVal">{c.before || '—'} → {c.after || '—'}</span>
                                        </div>
                                    ))
                                    : (selected.changes || []).map((c, i) => {
                                        const parsed = parseChange(c);
                                        return parsed ? (
                                            <div className="ledger__diffRow" key={i}>
                                                <span className="ledger__diffField">{parsed.field}</span>
                                                <span className="ledger__diffVal">{parsed.before} → {parsed.after}</span>
                                            </div>
                                        ) : (
                                            <div className="ledger__diffRow" key={i}>
                                                <span className="ledger__diffField">—</span>
                                                <span className="ledger__diffVal">{c}</span>
                                            </div>
                                        );
                                    })}
                            </div>
                            {!hasStructured &&
                                <em className="ledger__hint">Parsed from text — this alert predates structured change records.</em>}
                        </div>

                        <div className="ledger__panelSection">
                            <span className="ledger__panelLabel">Why this matters</span>
                            <ul className="ledger__whyList">
                                {why.reasons.map((r, i) => <li key={i}>{r}</li>)}
                            </ul>
                            <p className="ledger__whyAction">{why.action}</p>
                        </div>

                        <div className="ledger__panelSection">
                            <span className="ledger__panelLabel">Provenance</span>
                            <p className="ledger__prov">
                                {SOURCE_PROVENANCE[selectedSource] || 'Monitoring sweep'}
                                {selected.lastChecked ? ` · last checked ${moment(selected.lastChecked).format('MMM DD, YYYY, HH:mm')}` : ''}
                                {selected.nextSweep ? ` · next sweep ${moment(selected.nextSweep).format('MMM DD, YYYY')}` : ''}
                            </p>
                        </div>

                        <div className="ledger__panelSection">
                            <span className="ledger__panelLabel">Notes</span>
                            {selected.notes && selected.notes.length > 0 ? (
                                <ul className="ledger__noteList">
                                    {selected.notes.map((n) => (
                                        <li className="ledger__noteItem" key={n.id}>
                                            <div className="ledger__noteMeta">
                                                <span className="ledger__noteAuthor">{n.author || 'Unknown'}</span>
                                                <span className="ledger__noteDate">
                                                    {n.createdAt ? moment(n.createdAt).format('MMM DD, YYYY, HH:mm') : ''}
                                                </span>
                                                {!readOnly && (n.author || '').toLowerCase() === currentUserEmail &&
                                                    <button className="ledger__noteHide" title="Delete this note"
                                                        onClick={() => hideNote(n.id)}>Delete</button>}
                                            </div>
                                            <p className="ledger__noteText">{n.text}</p>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="ledger__noteEmpty">No notes yet.</p>
                            )}
                            {!readOnly && (
                                <>
                                    <textarea
                                        className="ledger__noteInput"
                                        placeholder="Add a note…"
                                        value={noteText}
                                        onChange={(e) => setNoteText(e.target.value)}
                                    />
                                    <div className="ledger__noteRow">
                                        <button
                                            className="ledger__noteSaveBtn"
                                            disabled={savingNote || !noteText.trim()}
                                            onClick={addNote}
                                        >
                                            {savingNote ? 'Adding…' : 'Add note'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="ledger__panelActions">
                            {selectedAcked
                                ? <span className="ledger__ackedBy">Acknowledged{selected.acknowledgedBy ? ` · ${selected.acknowledgedBy}` : ''}{selected.acknowledgedAt ? ` · ${moment(selected.acknowledgedAt).format('MMM DD')}` : ''}</span>
                                : !readOnly && <button className="ledger__ackBtn" onClick={() => { ackOne(selected, noteText); setSelected(null); }}>Acknowledge</button>}
                            {selected.applicationId && (
                                <button className="ledger__ghostBtn" onClick={() => history.push(applicantHref(selected))}>View application</button>
                            )}
                        </div>
                    </aside>
                </>
            )}

            <MonitoringSettings
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                getConfig={getSeverityConfig}
                updateConfig={updateSeverityConfig}
                getFieldCatalog={getFieldCatalog}
                getWatchedFields={getWatchedFields}
                updateWatchedFields={updateWatchedFields}
            />
        </div>
    );
};

export default MonitoringLedger;
